import { formatMonthYearAsHumanReadable } from '@bestseller/retail-planning.styletimeline';
import { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { BuyingEventCategoriesRenderer } from 'src/components/molecules/BuyingEventCategoriesRenderer/BuyingEventCategoriesRenderer';
import { BuyingEventManagementActionsRenderer } from 'src/components/molecules/BuyingEventManagementActionsRenderer/BuyingEventManagementActionsRenderer';
import { BuyingEventRow } from 'src/domain/table/buying-event-row';
import { useBuyingEventQuery } from '../queries/useBuyingEventQuery';

function mapToCategores(
    plannedOnIds: number[] | undefined,
    planningLevelList: { id: number; name: string }[] | undefined
): string[] {
    return (
        (plannedOnIds
            ?.map((id) => planningLevelList?.find((item) => item.id === id)?.name)
            .filter(Boolean) as string[]) ?? []
    );
}

export function useBuyingEventManagementTable() {
    const { loading, events, planningLevelList, refetch, error } = useBuyingEventQuery();

    const rows: BuyingEventRow[] = useMemo(() => {
        return (events ?? []).map((event) => {
            const categories = mapToCategores(event.plannedOnIds, planningLevelList);

            return {
                ...event,
                categories: categories.length === planningLevelList?.length ? ['All'] : categories,
            } as BuyingEventRow;
        });
    }, [events, planningLevelList]);

    const columns = useMemo(() => {
        return [
            {
                field: 'name',
                headerName: 'Event Name',
                flex: 3,
                valueFormatter({ value, data }) {
                    if (data?.isDefault) {
                        return `${value} (default)`;
                    }
                    return value ?? '';
                },
            },
            {
                field: 'orderMonth',
                headerName: 'Date',
                sort: 'desc',
                valueFormatter: ({ value }) => {
                    if (!value) return '';
                    return formatMonthYearAsHumanReadable(value);
                },
            },
            {
                field: 'deliveryRange',
                headerName: 'Delivery range',
                flex: 2,
                valueGetter: (params) => {
                    return {
                        start: params.data?.buyingStart ?? null,
                        end: params.data?.buyingEnd ?? null,
                    };
                },
                valueFormatter: ({ value }) => {
                    const { start, end } = value;
                    if (!start && !end) return '';

                    const startStr = start ? formatMonthYearAsHumanReadable(start) : '';
                    const endStr = end ? formatMonthYearAsHumanReadable(end) : '';
                    return `${startStr} - ${endStr}`;
                },
            },
            {
                field: 'categories',
                headerName: 'Categories',
                cellRenderer: BuyingEventCategoriesRenderer,
                flex: 3,
            },
            {
                headerName: 'Actions',
                cellRenderer: BuyingEventManagementActionsRenderer,
            },
        ] as ColDef<BuyingEventRow>[];
    }, []);

    return {
        loading,
        data: rows,
        columns,
        refetch,
        planningLevelList,
        error,
    };
}
